import { useAuth, useUser } from '@clerk/react';
import {
  Button,
  Input,
  InputPassword,
  InputWrapper,
  passwordRegex,
} from '@vrplatform/components';
import {
  useApiMutation,
  useInvalidateApiQueries,
  useMe,
  useTeam,
  useTeamMembership,
  useTrpcMutation,
  useIsOwnerPortalPreview,
} from '@vrplatform/data';
import { EmailIcon, RocketIcon } from '@vrplatform/icons';
import { Modal } from '@vrplatform/components-legacy';
import {
  showErrorNotification,
  showSuccessNotification,
  useAppName,
} from '@vrplatform/theme';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import {
  Box,
  Card,
  Center,
  Group,
  Stack,
  Switch,
  Text,
  Title,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';

type FormInputs = {
  firstName: string;
  lastName: string;
  password: string;
  passwordConfirm: string;
  emailNotifications: boolean;
};

export function useIsWelcomeModalOpen() {
  const { user } = useUser();
  const isOwnerPortalPreview = useIsOwnerPortalPreview();

  const [resetPasswordParam] = useQueryState('resetPassword', parseAsString);

  if (isOwnerPortalPreview) return false;

  return (
    !!user?.unsafeMetadata?.needPasswordChange ||
    !!user?.unsafeMetadata?.need_password_change ||
    !!resetPasswordParam
  );
}

export const WelcomeUserModal = () => {
  const theme = useMantineTheme();
  const { user } = useUser();
  const [{ id: teamId }] = useTeam();

  const { mutate: updateClerkUser } = useTrpcMutation('updateClerkUser', {
    errorMessage: {
      title: 'Failed to update password',
      message: (error) => error.message.replace('Clerk Error: ', ''),
    },
  });
  const { userId: clerkUserId } = useAuth();
  const {
    firstName: currentFirstName,
    lastName: currentLastName,
    type: meType,
  } = useMe();
  const membership = useTeamMembership();
  const { mutateAsync: updateProfile } = useApiMutation('put', '/me');
  const { mutateAsync: updateNotifications } = useApiMutation(
    'put',
    '/me/notifications'
  );
  const invalidateMe = useInvalidateApiQueries({
    groups: ['teams'],
    includeUniqueQueryKeys: ['me'],
  });

  const [resetPasswordParam, setResetPasswordParam] = useQueryState(
    'resetPassword',
    parseAsString
  );

  const opened = useIsWelcomeModalOpen();

  const type = resetPasswordParam ? 'password-reset' : 'welcome';

  const { primaryColor, colors } = useMantineTheme();
  const { appName } = useAppName();

  const { control, handleSubmit, setError, formState } = useForm<FormInputs>({
    defaultValues: {
      emailNotifications:
        membership?.notifications.ownerStatementPublished ?? true,
      password: '',
      passwordConfirm: '',
      firstName: currentFirstName ?? '',
      lastName: currentLastName ?? '',
    },
  });

  const submit = async ({
    password,
    firstName,
    lastName,
    passwordConfirm,
    emailNotifications,
  }: FormInputs) => {
    if (!user) {
      return showErrorNotification({
        title: 'Internal Error',
        message: 'User not found. Please refresh the page.',
        color: 'yellow',
      });
    }

    if (password.trim() !== passwordConfirm.trim()) {
      return setError('passwordConfirm', {
        type: 'validation',
        message: 'Password and Confirm Password must be the same.',
      });
    }

    if (!clerkUserId) {
      return showErrorNotification({
        title: 'Failed to update password',
        message:
          'Please refresh the page. If the problem persists, contact support.',
      });
    }

    try {
      await updateClerkUser({
        teamId,
        clerkUserId,
        data: {
          pw: password,
          first_name: type === 'welcome' ? firstName : undefined,
          last_name: type === 'welcome' ? lastName : undefined,
          unsafe_metadata: {},
        },
      });

      if (type === 'welcome') {
        await Promise.all([
          updateProfile({
            body: {
              firstName,
              lastName,
            },
          }),
          updateNotifications({
            body: {
              ownerStatementPublished: emailNotifications,
            },
          }),
        ]);
      }

      await Promise.all([user.reload(), invalidateMe()]);
      await setResetPasswordParam(null);

      showSuccessNotification({
        title: type === 'password-reset' ? 'Success' : 'Welcome to VRP!',
        id: `${type}-success-notification`,
        message:
          type === 'password-reset'
            ? 'Your password was changed.'
            : "You're all set up and ready to go.",
      });
    } catch {
      // Mutation hooks surface the error; keeping state unchanged leaves the form retryable.
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => null}
      withCloseButton={false}
      size={500}
      styles={{
        body: {
          paddingBlock: rem(40),
          paddingInline: rem(40),
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(submit, (errors) => {
          console.error(errors);
        })}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: rem(48),
        }}
      >
        {type === 'welcome' && (
          <Center>
            <RocketIcon
              color={colors[primaryColor][6]}
              size={45}
              strokeWidth={1}
            />
          </Center>
        )}
        <Box>
          <Title ta="center" size={rem(20)} mb={rem(24)}>
            {type === 'welcome' ? `Welcome to ${appName}` : 'Reset password'}
          </Title>
          <Text c="neutral" ta="center" size="sm">
            {type === 'welcome'
              ? 'We are thrilled to have you on board. Setup your name, password and email preferences.'
              : 'Please enter your new password. You will logged in automatically and can start using the platform.'}
          </Text>
        </Box>

        <>
          <Stack>
            {type === 'welcome' && (
              <Group
                wrap="nowrap"
                align="flex-start"
                sx={{
                  '> div': {
                    flex: 1,
                  },
                }}
              >
                <Controller
                  control={control}
                  name="firstName"
                  defaultValue=""
                  rules={{
                    required: {
                      value: true,
                      message: 'Please enter your first name.',
                    },
                  }}
                  render={({
                    field: { value, onChange, name },
                    fieldState: { error },
                  }) => {
                    return (
                      <InputWrapper
                        label={'First name'}
                        error={error?.message}
                        required
                      >
                        <Input
                          value={value}
                          onChange={onChange}
                          name={name}
                          placeholder="First name"
                          error={error?.message}
                          autoComplete="given-name"
                          type="text"
                          required
                        />
                      </InputWrapper>
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="lastName"
                  defaultValue=""
                  rules={{
                    required: {
                      value: true,
                      message: 'Please enter your first name.',
                    },
                  }}
                  render={({
                    field: { value, onChange, name },
                    fieldState: { error },
                  }) => {
                    return (
                      <InputWrapper
                        label={'Last name'}
                        error={error?.message}
                        required
                      >
                        <Input
                          value={value}
                          onChange={onChange}
                          name={name}
                          placeholder="Last name"
                          error={error?.message}
                          autoComplete="family-name"
                          type="text"
                          required
                        />
                      </InputWrapper>
                    );
                  }}
                />
              </Group>
            )}

            <Controller
              control={control}
              name="password"
              rules={{
                required: {
                  value: true,
                  message: 'Please enter a password.',
                },
                pattern: {
                  value: passwordRegex,
                  message:
                    'Please enter a lower/uppercase letter, one number, one special symbol and at least 8 characters.',
                },
              }}
              render={({
                field: { value, onChange, name },
                fieldState: { error },
              }) => {
                return (
                  <InputWrapper
                    label={'Password'}
                    error={error?.message}
                    required
                  >
                    <InputPassword
                      value={value || ''}
                      onChange={onChange}
                      name={name}
                      error={error?.message}
                      autoComplete="new-password"
                    />
                  </InputWrapper>
                );
              }}
            />

            <Controller
              control={control}
              name="passwordConfirm"
              rules={{
                required: {
                  value: true,
                  message: 'Please confirm your password.',
                },
              }}
              render={({
                field: { value, onChange, name },
                fieldState: { error },
              }) => {
                return (
                  <InputWrapper
                    label={'Confirm password'}
                    error={error?.message}
                    required
                  >
                    <InputPassword
                      value={value || ''}
                      onChange={onChange}
                      name={name}
                      error={error?.message}
                      withPopover={false}
                      autoComplete="off"
                    />
                  </InputWrapper>
                );
              }}
            />
          </Stack>

          {type === 'welcome' && meType === 'owner' && (
            <Controller
              control={control}
              name="emailNotifications"
              defaultValue={true}
              render={({ field: { value, onChange, name }, fieldState }) => {
                return (
                  <Card
                    withBorder
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      gap: rem(12),
                      borderColor: fieldState.error?.message
                        ? theme.colors.red[6]
                        : undefined,
                    })}
                  >
                    <Center>
                      <EmailIcon size={18} />
                    </Center>
                    <Switch
                      checked={!!value}
                      onChange={onChange}
                      name={name}
                      label="I want to receive important platform notifications by email"
                      labelPosition="left"
                      styles={{
                        body: {
                          alignItems: 'center',
                        },
                      }}
                      color={theme.colors[theme.primaryColor][6]}
                      error={!!fieldState.error}
                    />
                  </Card>
                );
              }}
            />
          )}
        </>

        <Button
          variant="primary"
          type="submit"
          loading={formState.isSubmitting}
        >
          {type === 'welcome' ? `Start with ${appName}` : 'Reset password'}
        </Button>
      </Box>
    </Modal>
  );
};
